import React, { useEffect, useState } from 'react';
import { IconBank, IconOpenBanking } from '@americanexpress/dls-react';

import useAsyncCallback from 'src/hooks/useAsyncCallback';
import { AccountDrawerType, AccountModel } from 'src/types/account.model';
import AccountService from 'src/services/account.service';
import { ManualAccountType } from 'src/types/manualAccount.model';
import ManualAccountService from '../../../../../services/manualAccount.service';
import { DebtModel } from 'src/types/debt.model';

import ListAccountsViewTemplate from '../listAccountsViewTemplate/ListAccountsViewTemplate';
import Callout from '../../../atoms/callout/Callout';
import WhyShouldYouConnectTooltip from '../../../atoms/tooltips/whyYouShouldConnectTooltip/WhyShouldYouConnectTooltip';
import LiteralsService from 'src/services/literals/LiteralsService';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { DLSSize } from 'src/types/dls-size.enum';
import { AddAccountListTemplate } from 'src/types/view-models/listAccounts/listAccountsViewModel.view.model';
import LoansAndDebtsListAccountsViewModel from 'src/types/view-models/listAccounts/loansAndDebts.view.model';
import Selectors from 'src/utils/selectors';

const addAccountListTemplate: AddAccountListTemplate = [
    {
        title: LiteralsService.getLiteral('Onboarding.loansAndDebt.addMortgageTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.loansAndDebt.addMortgageSubtitle'),
        manualAccountType: ManualAccountType.Mortgage
    },
    {
        title: LiteralsService.getLiteral('Onboarding.loansAndDebt.addStudentLoanTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.loansAndDebt.addStudentLoanSubtitle'),
        manualAccountType: ManualAccountType.StudentLoan
    },
    {
        title: LiteralsService.getLiteral('Onboarding.loansAndDebt.addPersonalLoanTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.loansAndDebt.addPersonalLoanSubtitle'),
        manualAccountType: ManualAccountType.PersonalLoan
    },
    {
        title: LiteralsService.getLiteral('Onboarding.loansAndDebt.addAutoLoanTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.loansAndDebt.addAutoLoanSubtitle'),
        manualAccountType: ManualAccountType.AutoLoan
    },
    {
        title: LiteralsService.getLiteral('Onboarding.loansAndDebt.addCreditCardTitle'),
        subtitle: LiteralsService.getLiteral('Onboarding.loansAndDebt.addCreditCardSubtitle'),
        manualAccountType: ManualAccountType.CreditCardDebt
    },
    {
        title: LiteralsService.getLiteral('Onboarding.loansAndDebt.otherTitle'),
        manualAccountType: ManualAccountType.OtherDebt
    }
];

const callout = (
    <Callout
        icon={<IconBank size={DLSSize.MEDIUM} />}
        heading={LiteralsService.getLiteral('Onboarding.cashAndBanking.calloutHeading')}
    >
        <Typography variant={TypographyVariants.BODY}>
            {LiteralsService.getLiteral('Onboarding.cashAndBanking.calloutBody')}
        </Typography>
        <WhyShouldYouConnectTooltip withText />
    </Callout>
);

const formCallout = (
    <Callout
        icon={<IconOpenBanking size={DLSSize.MEDIUM} />}
        heading={LiteralsService.getLiteral('Onboarding.loansAndDebt.formCalloutHeading')}
        body={LiteralsService.getLiteral('Onboarding.loansAndDebt.formCalloutBody')}
    />
);

export default function LoansAndDebtsListAccountsView({
    user,
    back,
    next,
    handleReloadUser
}: LoansAndDebtsListAccountsViewModel): JSX.Element {
    const [visibleAccounts, setVisibleAccounts] = useState<AccountModel[]>([]);
    const [hiddenAccounts, setHiddenAccounts] = useState<AccountModel[]>([]);
    const [nonDrawerTypeAccounts, setNonDrawerTypeAccounts] = useState<AccountModel[]>();
    const [debts, setDebts] = useState<DebtModel[]>([]);
    const [loading, setLoading] = useState(true);

    const refreshAccounts = useAsyncCallback(async (): Promise<void> => {
        const [newVisibleAccounts, newHiddenAccounts] = await AccountService.getLoanAndDebtConnectedAccounts(user);
        const nonDrawerTypeAccounts = await AccountService.getNonLoanAndDebtConnectedAccounts(user);
        const { newDebts } = await ManualAccountService.getAccounts();
        setVisibleAccounts(newVisibleAccounts);
        setHiddenAccounts(newHiddenAccounts);
        setNonDrawerTypeAccounts(nonDrawerTypeAccounts);
        setDebts(newDebts.filter((e) => !e.accountId));
        setLoading(false);
    }, []);

    useEffect(() => {
        void refreshAccounts();
    }, [user.accounts.length]);

    if (loading) return <></>;

    return (
        <ListAccountsViewTemplate
            progressIndex={6}
            accountDrawerType={AccountDrawerType.LoanAndDebt}
            title={LiteralsService.getLiteral('Onboarding.loansAndDebt.title')}
            listAccountsHeading={LiteralsService.getLiteral('Onboarding.loansAndDebt.listAccountsHeading')}
            ManualAccountType={ManualAccountService.loansAndDebtsAccountTypes}
            visibleAccounts={visibleAccounts}
            hiddenAccounts={hiddenAccounts}
            nonDrawerTypeAccounts={nonDrawerTypeAccounts}
            investmentHoldings={[]}
            debts={debts}
            addAccountListTemplate={addAccountListTemplate}
            callout={callout}
            formCallout={formCallout}
            refreshAccounts={refreshAccounts}
            back={back}
            next={next}
            handleReloadUser={handleReloadUser}
            data-cy={Selectors.PlanAhead.LOANS_AND_DEBTS}
        />
    );
}
